import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";

const modalStack = [];
let escListenerAttached = false;
let openCount = 0;

const getTop = () => modalStack[modalStack.length - 1];

const attachEscListener = () => {
  if (escListenerAttached) return;
  document.addEventListener("keydown", handleEscGlobal, true);
  escListenerAttached = true;
};

const detachEscListenerIfIdle = () => {
  if (!escListenerAttached) return;
  if (modalStack.length === 0) {
    document.removeEventListener("keydown", handleEscGlobal, true);
    escListenerAttached = false;
  }
};

const handleEscGlobal = (e) => {
  if (e.key !== "Escape") return;
  const top = getTop();
  if (top && typeof top.close === "function") {
    // Close only the top-most modal
    top.close();
    // Prevent other handlers from seeing this ESC (belt & suspenders)
    e.stopImmediatePropagation?.();
    e.stopPropagation();
    e.preventDefault();
  }
};

const pushModal = (id, close) => {
  modalStack.push({ id, close });
  attachEscListener();
};

const updateModalClose = (id, close) => {
  const idx = modalStack.findIndex((m) => m.id === id);
  if (idx >= 0) modalStack[idx].close = close;
};

const popModal = (id) => {
  const idx = modalStack.findIndex((m) => m.id === id);
  if (idx >= 0) modalStack.splice(idx, 1);
  detachEscListenerIfIdle();
};

const lockScroll = () => {
  if (openCount === 0) {
    document.body.style.overflow = "hidden";
  }
  openCount += 1;
};

const unlockScroll = () => {
  openCount = Math.max(0, openCount - 1);
  if (openCount === 0) {
    document.body.style.overflow = "";
  }
};

const BuilderModal = ({
  showCloseBtn = true,
  isOpen,
  onClose,
  modalName,
  children,
  customStyleClsName = "",
  headerStyle = {},
  bodyStyle = {},
  backdropStyle = {}
}) => {
  const modalRef = useRef(null);
  const modalId = useRef(Symbol("modalId"));

  /* Manage stack membership + scroll lock */
  useEffect(() => {
    if (isOpen) {
      pushModal(modalId.current, onClose);
      lockScroll();

      // Animation hook
      requestAnimationFrame(() => {
        modalRef.current?.classList.add("modal-open");
      });
    }

    return () => {
      if (isOpen) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        popModal(modalId.current);
        unlockScroll();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      updateModalClose(modalId.current, onClose);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="builder-modal__backdrop"
      style={backdropStyle}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="builder-modal__wrapper">
        <div
          ref={modalRef}
          className={`builder-modal__container ${customStyleClsName}`}
          role="dialog"
          aria-modal="true"
          aria-label={typeof modalName === "string" ? modalName : undefined}
        >
          <div className="builder-modal__header" style={headerStyle}>
            <span>{modalName}</span>
            {showCloseBtn && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close modal"
                className="section_close_btn"
                style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
              >
                <MaterialIcon name="close" className="section_close_icon" />
              </button>
            )}
          </div>
          <div className="builder-modal__body" style={bodyStyle}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default BuilderModal;
